/**
 * Отделение, привязанное к координатору вида НС
 */
export interface IAdverseEventResponsibleDepartment {
  /** Уникальный идентификатор отделения */
  id: string
  /** Полное наименование отделения */
  name: string
  /** Краткое наименование отделения */
  short_name?: string
}

/**
 * Базовая информация о сотруднике-координаторе
 */
export interface IAdverseEventResponsibleEmployeeInfo {
  /** Идентификатор сотрудника (UUID) */
  id: string
  /** Имя */
  first_name: string
  /** Фамилия */
  last_name: string
  /** Отчество */
  middle_name?: string
  /** Ссылка на аватар сотрудника */
  avatar?: string
  /** Должность сотрудника */
  position?: {
    id?: string
    name: string
  }
  [key: string]: any
}

/**
 * Запись ответственного сотрудника (координатора) по виду НС
 */
export interface IAdverseEventResponsibleEmployee {
  /** ID записи связки ответственного */
  id: number
  /** Флаг: основной (по умолчанию) координатор для вида НС */
  is_default?: boolean
  /** Список отделений, за которые отвечает данный координатор */
  departments?: IAdverseEventResponsibleDepartment[]
  /** Данные сотрудника */
  employee: IAdverseEventResponsibleEmployeeInfo
}

/**
 * Узел дерева видов нежелательных событий (AdverseEventTypeTreeNode)
 */
export interface IAdverseEventTypeTreeNode {
  questionnaire_ids?: number[]
  /** Идентификатор категории/вида НС */
  id: string
  /** ID родительской категории в дереве (null для корневых узлов) */
  parent_id?: string | null
  /** Название вида НС */
  name: string
  /** Описание вида НС */
  description?: string
  /** Флаг активности */
  is_active?: boolean
  /** Из базы ДаоМед (read-only с бэка) */
  is_daomed_main?: boolean
  /** Оценка последствий по умолчанию: 0 - Несущественные, 1 - Легкие, 2 - Средние, 3 - Значительные, 4 - Катастрофические */
  consequence?: number | null
  /** Порядок сортировки при отображении */
  order?: number
  /** Список назначенных координаторов (ответственных сотрудников) */
  responsible_employees?: IAdverseEventResponsibleEmployee[]
  /** Последствия в разрезе отделений */
  department_consequences?: any[]
  /** Секция стандарта контроля качества (СМК) */
  quality_control_section?: any
  /** Дочерние виды нежелательных событий */
  children?: IAdverseEventTypeTreeNode[]
  [key: string]: any
}

/**
 * Опция фильтрации отображения видов НС в настройках
 */
export interface IAdverseEventTypeSettingsOption {
  /** Значение фильтра ('all' | 'custom' | 'expert') */
  value: string
  /** Человекочитаемое название */
  label: string
}

/**
 * Конфигурация настроек нежелательных событий
 */
export interface IAdverseEventSettings {
  /** Режим отображения видов событий ('all' | 'custom' | 'expert') */
  event_types?: string
  [key: string]: any
}

/**
 * Данные ответственного координатора при создании/обновлении вида НС
 */
export interface IAdverseEventTypeResponsiblePayload {
  /** ID существующей связки (если обновляется) */
  id?: number
  /** Флаг основного координатора */
  is_default?: boolean
  /** Идентификатор сотрудника */
  employee: {
    id: string
  }
  /** Идентификаторы закреплённых отделений */
  departments?: {
    id: string
  }[]
}

/**
 * Полезная нагрузка (payload) для создания нового вида НС
 */
export interface IAdverseEventTypeCreatePayload {
  /** ID родительского узла (null для корневого уровня) */
  parent_id?: string | null
  /** Наименование вида */
  name: string
  /** Описание вида */
  description?: string
  /** Активность вида */
  is_active?: boolean
  /** Уровень последствий по умолчанию */
  consequence?: number | null
  /** Порядковый номер сортировки */
  order?: number
  /** Назначенные ответственные координаторы */
  responsible_employees?: IAdverseEventTypeResponsiblePayload[]
  /** Назначенные ответственные должности */
  responsible_positions?: any[]
  [key: string]: any
}

/**
 * Полезная нагрузка (payload) для обновления существующего вида НС
 */
export interface IAdverseEventTypeUpdatePayload {
  /** ID родительского узла */
  parent_id?: string | null
  /** Наименование вида */
  name?: string
  /** Описание вида */
  description?: string
  /** Активность вида */
  is_active?: boolean
  /** Уровень последствий по умолчанию */
  consequence?: number | null
  /** Порядковый номер сортировки */
  order?: number
  /** Назначенные ответственные координаторы */
  responsible_employees?: IAdverseEventTypeResponsiblePayload[]
  /** Назначенные ответственные должности */
  responsible_positions?: any[]
  [key: string]: any
}

// ==========================================
// Пороги вероятности и Матрица рисков
// ==========================================

/**
 * Расчетный период для подсчета количества событий одного вида
 */
export type TProbabilityPeriod = 'day' | 'current' | 'month' | 'quarter' | 'year'

/**
 * Справочник расчетных периодов порогов вероятности
 */
export const PROBABILITY_PERIOD_OPTIONS: Array<{ value: TProbabilityPeriod; label: string }> = [
  { value: 'day', label: 'День' },
  { value: 'current', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'year', label: 'Год' },
]

/**
 * Сущность порога вероятности нежелательного события (AdverseEventProbabilityThreshold)
 */
export interface IAdverseProbabilityThreshold {
  /** Уникальный идентификатор порога */
  id: number
  /** Уровень вероятности: 0 - Практически исключено, 1 - Низкая, 2 - Средняя, 3 - Выше средней, 4 - Высокая */
  probability: number
  /** Текстовое наименование вероятности (только для чтения с бэкенда) */
  probability_display?: string
  /** Пороговое количество событий одного вида за выбранный период (>= 0) */
  threshold: number
  /** Расчетный период: 'day' | 'current' | 'month' | 'quarter' | 'year' */
  period: TProbabilityPeriod
  /** Текстовое наименование периода (только для чтения с бэкенда) */
  period_display?: string
}

/**
 * Полезная нагрузка для создания нового порога вероятности
 */
export interface IAdverseProbabilityThresholdCreatePayload {
  /** Уровень вероятности (0..4, уникальный в системе) */
  probability: number
  /** Пороговое количество событий (целое число >= 0) */
  threshold: number
  /** Расчетный период */
  period: TProbabilityPeriod
}

/**
 * Полезная нагрузка для обновления существующего порога вероятности
 */
export interface IAdverseProbabilityThresholdUpdatePayload {
  /** Уровень вероятности */
  probability?: number
  /** Пороговое количество событий (целое число >= 0) */
  threshold?: number
  /** Расчетный период */
  period?: TProbabilityPeriod
}

/**
 * Ответ ручки получения справочников вероятностей и последствий (/api/adverse/probability-consequence/)
 */
export interface IProbabilityConsequenceResponse {
  /** Список кортежей [код, наименование] для вероятности */
  probability: [number, string][]
  /** Список кортежей [код, наименование] для последствий */
  consequence: [number, string][]
}

/**
 * Опция выбора для селектов вероятности / последствий
 */
export interface IRiskSelectOption {
  /** Числовой код */
  value: number
  /** Человекочитаемая метка */
  label: string
  /** Иконка (если есть) */
  icon?: string
}

/**
 * Описание ячейки матрицы рисков (Вероятность × Последствия)
 */
export interface IRiskMatrixCell {
  /** Уровень вероятности (0..4) */
  probability: number
  /** Уровень последствий (0..4) */
  consequence: number
  /** Итоговый уровень риска: 1 - Низкий, 2 - Средний, 3 - Высокий, 0 - Не определен */
  riskLevel: number
  /** Текстовое название уровня риска ('Низкий' | 'Средний' | 'Высокий') */
  riskName: string
  /** Hex-код цвета фона ячейки */
  riskColor: string
}


